import { useState, useEffect } from "react";
import {
  Sparkles, Image, Calendar, BarChart3, Lightbulb, Video, Send,
  Copy, Download, Plus, Loader2, Instagram, Facebook, Twitter,
  Megaphone, FileText, Clock, CheckCircle, TrendingUp, Eye,
  MousePointerClick, Share2, Target, Palette, Wand2, Film,
  PenTool, Hash, RefreshCw, Archive, Trash2, Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PLATFORM_FEATURES } from "@/lib/platform-features";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const PLATFORMS = [
  { value: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-500" },
  { value: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-500" },
  { value: "twitter", label: "Twitter/X", icon: Twitter, color: "text-sky-400" },
];

const CONTENT_TYPES = [
  { value: "post", label: "Post Feed" },
  { value: "story", label: "Story" },
  { value: "reel", label: "Reel/Vídeo" },
  { value: "carousel", label: "Carrossel" },
  { value: "video", label: "Vídeo Slideshow" },
];

const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--accent))", "#f59e0b", "#10b981", "#8b5cf6"];

const AdminMarketingTab = () => {
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState("ai_studio");
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [calendarItems, setCalendarItems] = useState<any[]>([]);
  const [aiLogs, setAiLogs] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);

  // AI Studio state
  const [aiType, setAiType] = useState("caption");
  const [aiPlatform, setAiPlatform] = useState("instagram");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResult, setAiResult] = useState("");
  const [aiImage, setAiImage] = useState<string | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(
    PLATFORM_FEATURES.map(f => f.label)
  );

  // Campaign form state
  const [newCampaign, setNewCampaign] = useState({
    title: "", description: "", platform: "instagram", content_type: "post",
    caption: "", hashtags: ""
  });

  // Calendar form
  const [newCalendarItem, setNewCalendarItem] = useState({
    title: "", date: "", time: "", platform: "instagram", notes: ""
  });

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [campRes, calRes, logRes, metRes] = await Promise.all([
      supabase.from("admin_marketing_campaigns").select("*").order("created_at", { ascending: false }),
      supabase.from("admin_marketing_calendar").select("*").order("date", { ascending: true }),
      supabase.from("admin_marketing_ai_log").select("*").order("created_at", { ascending: false }).limit(50),
      supabase.from("admin_marketing_metrics").select("*").order("date", { ascending: false }).limit(100),
    ]);
    setCampaigns(campRes.data || []);
    setCalendarItems(calRes.data || []);
    setAiLogs(logRes.data || []);
    setMetrics(metRes.data || []);
    setLoading(false);
  };

  const generateAI = async () => {
    if (!aiPrompt && aiType !== "ideas" && aiType !== "calendar") {
      toast({ title: "Insira um prompt", variant: "destructive" });
      return;
    }
    setAiGenerating(true);
    setAiResult("");
    setAiImage(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-platform-marketing", {
        body: {
          type: aiType,
          prompt: aiPrompt,
          platform: aiPlatform,
          features: selectedFeatures,
        },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setAiResult(data.content || "");
      if (data.image) setAiImage(data.image);

      // Log
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("admin_marketing_ai_log").insert({
          prompt: aiPrompt || aiType,
          result_type: aiType,
          result_content: data.content || "",
          result_image_url: data.image || null,
          model_used: aiType === "image" ? "gemini-flash-image" : "gemini-flash",
          platform: aiPlatform,
          created_by: user.id,
        });
      }

      toast({ title: "Conteúdo gerado com sucesso!" });
    } catch (e: any) {
      toast({ title: "Erro ao gerar", description: e.message, variant: "destructive" });
    }
    setAiGenerating(false);
  };

  const saveCampaign = async () => {
    if (!newCampaign.title) {
      toast({ title: "Título obrigatório", variant: "destructive" });
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.from("admin_marketing_campaigns").insert({
      ...newCampaign,
      hashtags: newCampaign.hashtags.split(",").map(h => h.trim()).filter(Boolean),
      created_by: user.id,
    });
    if (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } else {
      toast({ title: "Campanha criada!" });
      setNewCampaign({ title: "", description: "", platform: "instagram", content_type: "post", caption: "", hashtags: "" });
      fetchAll();
    }
  };

  const updateCampaignStatus = async (id: string, status: string) => {
    await supabase.from("admin_marketing_campaigns").update({
      status,
      ...(status === "published" ? { published_at: new Date().toISOString() } : {}),
    }).eq("id", id);
    fetchAll();
    toast({ title: `Status atualizado para ${status}` });
  };

  const deleteCampaign = async (id: string) => {
    await supabase.from("admin_marketing_campaigns").delete().eq("id", id);
    fetchAll();
    toast({ title: "Campanha removida" });
  };

  const saveCalendarItem = async () => {
    if (!newCalendarItem.title || !newCalendarItem.date) {
      toast({ title: "Preencha título e data", variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("admin_marketing_calendar").insert(newCalendarItem);
    if (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } else {
      toast({ title: "Item adicionado ao calendário!" });
      setNewCalendarItem({ title: "", date: "", time: "", platform: "instagram", notes: "" });
      fetchAll();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copiado!" });
  };

  const getPlatformIcon = (platform: string) => {
    const p = PLATFORMS.find(pl => pl.value === platform);
    if (!p) return <Megaphone className="h-4 w-4" />;
    const Icon = p.icon;
    return <Icon className={`h-4 w-4 ${p.color}`} />;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: "bg-muted text-muted-foreground",
      scheduled: "bg-amber-500/20 text-amber-500",
      published: "bg-emerald-500/20 text-emerald-500",
      archived: "bg-secondary text-secondary-foreground",
      planned: "bg-blue-500/20 text-blue-500",
      created: "bg-purple-500/20 text-purple-500",
    };
    return <Badge className={`${colors[status] || "bg-muted"} text-xs`}>{status}</Badge>;
  };

  // Mock metrics data for charts
  const chartData = [
    { name: "Seg", impressions: 1200, clicks: 89, engagement: 45 },
    { name: "Ter", impressions: 1500, clicks: 120, engagement: 67 },
    { name: "Qua", impressions: 980, clicks: 75, engagement: 32 },
    { name: "Qui", impressions: 2100, clicks: 160, engagement: 98 },
    { name: "Sex", impressions: 1800, clicks: 140, engagement: 82 },
    { name: "Sáb", impressions: 2400, clicks: 210, engagement: 120 },
    { name: "Dom", impressions: 1600, clicks: 130, engagement: 70 },
  ];

  const platformDistribution = [
    { name: "Instagram", value: 45 },
    { name: "Facebook", value: 30 },
    { name: "Twitter/X", value: 25 },
  ];

  const filteredCampaigns = campaigns.filter(c =>
    c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.platform?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sections = [
    { key: "ai_studio", label: "Estúdio IA", icon: Sparkles },
    { key: "campaigns", label: "Campanhas", icon: Megaphone },
    { key: "calendar", label: "Calendário", icon: Calendar },
    { key: "image_gen", label: "Gerar Imagens", icon: Image },
    { key: "video_gen", label: "Gerar Vídeo", icon: Video },
    { key: "analytics", label: "Analytics", icon: BarChart3 },
    { key: "history", label: "Histórico IA", icon: Clock },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Marketing da Plataforma
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Crie conteúdo com IA, gerencie campanhas e acompanhe resultados
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
        </Button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-2">
        {sections.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              activeSection === s.key
                ? "bg-primary text-primary-foreground"
                : "bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            <s.icon className="h-3.5 w-3.5" />
            {s.label}
          </button>
        ))}
      </div>

      {/* ============ AI STUDIO ============ */}
      {activeSection === "ai_studio" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-primary" />
                Gerador de Conteúdo IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Tipo</label>
                  <Select value={aiType} onValueChange={setAiType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="caption"><PenTool className="h-3 w-3 inline mr-1" />Legendas</SelectItem>
                      <SelectItem value="ideas"><Lightbulb className="h-3 w-3 inline mr-1" />Ideias</SelectItem>
                      <SelectItem value="calendar"><Calendar className="h-3 w-3 inline mr-1" />Calendário</SelectItem>
                      <SelectItem value="ad_copy"><Target className="h-3 w-3 inline mr-1" />Anúncios</SelectItem>
                      <SelectItem value="video_script"><Film className="h-3 w-3 inline mr-1" />Roteiro Vídeo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Plataforma</label>
                  <Select value={aiPlatform} onValueChange={setAiPlatform}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          <p.icon className={`h-3 w-3 inline mr-1 ${p.color}`} />{p.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="all">Todas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Prompt / Contexto
                </label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={
                    aiType === "caption" ? "Ex: Lançamento do novo recurso de Cardápio Digital com QR Code..."
                    : aiType === "ideas" ? "Ex: Foco em atrair novos restaurantes..."
                    : aiType === "ad_copy" ? "Ex: Campanha de Black Friday para planos anuais..."
                    : aiType === "video_script" ? "Ex: Vídeo de 30 segundos mostrando o PDV em ação..."
                    : "Descreva o tema ou foco do conteúdo..."
                  }
                  rows={4}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Recursos para destacar ({selectedFeatures.length})
                </label>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 bg-secondary/30 rounded-lg">
                  {PLATFORM_FEATURES.map(f => (
                    <button
                      key={f.key}
                      onClick={() => {
                        setSelectedFeatures(prev =>
                          prev.includes(f.label)
                            ? prev.filter(l => l !== f.label)
                            : [...prev, f.label]
                        );
                      }}
                      className={`text-[10px] px-2 py-0.5 rounded-full transition-all ${
                        selectedFeatures.includes(f.label)
                          ? "bg-primary/20 text-primary border border-primary/30"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={generateAI}
                disabled={aiGenerating}
                className="w-full"
              >
                {aiGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Gerar com IA</>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Resultado
              </CardTitle>
            </CardHeader>
            <CardContent>
              {aiGenerating ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Gerando conteúdo criativo...</p>
                </div>
              ) : aiResult ? (
                <div className="space-y-3">
                  <div className="bg-secondary/30 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{aiResult}</pre>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(aiResult)}>
                      <Copy className="h-3 w-3 mr-1" />Copiar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      setNewCampaign(prev => ({ ...prev, caption: aiResult }));
                      setActiveSection("campaigns");
                      toast({ title: "Texto copiado para nova campanha" });
                    }}>
                      <Send className="h-3 w-3 mr-1" />Usar em Campanha
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground space-y-2">
                  <Sparkles className="h-10 w-10 opacity-30" />
                  <p className="text-sm">O resultado aparecerá aqui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ IMAGE GEN ============ */}
      {activeSection === "image_gen" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                Gerar Imagem com IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Plataforma</label>
                <Select value={aiPlatform} onValueChange={setAiPlatform}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Descreva a imagem que deseja
                </label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ex: Post moderno para Instagram mostrando um dashboard de restaurante com gráficos, cores vibrantes, estilo tech..."
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  "Post de lançamento com dashboard moderno",
                  "Story com destaque de recurso de delivery",
                  "Anúncio com antes/depois de gestão de restaurante",
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setAiPrompt(suggestion)}
                    className="text-[10px] p-2 rounded-lg bg-secondary/40 hover:bg-secondary text-muted-foreground hover:text-foreground transition-all text-left"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              <Button
                onClick={() => { setAiType("image"); generateAI(); }}
                disabled={aiGenerating}
                className="w-full"
              >
                {aiGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando imagem...</>
                ) : (
                  <><Image className="h-4 w-4 mr-2" />Gerar Imagem</>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preview da Imagem</CardTitle>
            </CardHeader>
            <CardContent>
              {aiGenerating ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Criando imagem...</p>
                </div>
              ) : aiImage ? (
                <div className="space-y-3">
                  <img src={aiImage} alt="Generated" className="w-full rounded-lg border border-border" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" asChild>
                      <a href={aiImage} download="viamenu-marketing.png">
                        <Download className="h-3 w-3 mr-1" />Baixar
                      </a>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(aiImage!)}>
                      <Copy className="h-3 w-3 mr-1" />Copiar URL
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground space-y-2">
                  <Image className="h-10 w-10 opacity-30" />
                  <p className="text-sm">A imagem gerada aparecerá aqui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ VIDEO GEN ============ */}
      {activeSection === "video_gen" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Film className="h-5 w-5 text-primary" />
                Gerar Roteiro de Vídeo Slideshow
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Plataforma</label>
                  <Select value={aiPlatform} onValueChange={setAiPlatform}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram Reels (9:16)</SelectItem>
                      <SelectItem value="facebook">Facebook (16:9)</SelectItem>
                      <SelectItem value="twitter">Twitter/X (16:9)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Duração</label>
                  <Select value={aiPrompt.includes("60") ? "60" : "30"} onValueChange={(v) => setAiPrompt(`${v} segundos`)}>
                    <SelectTrigger><SelectValue placeholder="Duração" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 segundos</SelectItem>
                      <SelectItem value="30">30 segundos</SelectItem>
                      <SelectItem value="60">60 segundos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Contexto adicional (opcional)
                </label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ex: Foco no recurso de PDV e como ele agiliza o atendimento..."
                  rows={3}
                />
              </div>

              <div className="bg-secondary/30 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">📐 Formatos suportados:</p>
                <p>• Instagram Reels/Stories: 1080x1920 (9:16)</p>
                <p>• Facebook Feed: 1920x1080 (16:9)</p>
                <p>• Twitter/X: 1920x1080 (16:9)</p>
                <p>• Feed quadrado: 1080x1080 (1:1)</p>
              </div>

              <Button
                onClick={() => { setAiType("video_script"); generateAI(); }}
                disabled={aiGenerating}
                className="w-full"
              >
                {aiGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando roteiro...</>
                ) : (
                  <><Video className="h-4 w-4 mr-2" />Gerar Roteiro de Vídeo</>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Roteiro Gerado</CardTitle>
            </CardHeader>
            <CardContent>
              {aiGenerating ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Criando roteiro...</p>
                </div>
              ) : aiResult && aiType === "video_script" ? (
                <div className="space-y-3">
                  <div className="bg-secondary/30 rounded-lg p-4 max-h-[500px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{aiResult}</pre>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(aiResult)}>
                      <Copy className="h-3 w-3 mr-1" />Copiar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground space-y-2">
                  <Film className="h-10 w-10 opacity-30" />
                  <p className="text-sm">O roteiro do vídeo aparecerá aqui</p>
                  <p className="text-[10px]">Inclui cenas, textos na tela, transições e narração</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ CAMPAIGNS ============ */}
      {activeSection === "campaigns" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="h-5 w-5" /> Nova Campanha
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  placeholder="Título da campanha"
                  value={newCampaign.title}
                  onChange={e => setNewCampaign(p => ({ ...p, title: e.target.value }))}
                />
                <Select value={newCampaign.platform} onValueChange={v => setNewCampaign(p => ({ ...p, platform: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    <SelectItem value="all">Todas</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={newCampaign.content_type} onValueChange={v => setNewCampaign(p => ({ ...p, content_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder="Legenda / Copy da campanha"
                value={newCampaign.caption}
                onChange={e => setNewCampaign(p => ({ ...p, caption: e.target.value }))}
                rows={3}
              />
              <div className="flex gap-3">
                <Input
                  placeholder="Hashtags (separadas por vírgula)"
                  value={newCampaign.hashtags}
                  onChange={e => setNewCampaign(p => ({ ...p, hashtags: e.target.value }))}
                  className="flex-1"
                />
                <Button onClick={saveCampaign}>
                  <Plus className="h-4 w-4 mr-1" />Criar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Campaign list */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar campanhas..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="grid gap-3">
            {filteredCampaigns.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">Nenhuma campanha ainda</CardContent></Card>
            ) : filteredCampaigns.map(c => (
              <Card key={c.id} className="hover:border-primary/20 transition-colors">
                <CardContent className="py-4 flex items-start gap-4">
                  <div className="mt-1">{getPlatformIcon(c.platform)}</div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm truncate">{c.title}</h4>
                      {getStatusBadge(c.status)}
                      <Badge variant="outline" className="text-[10px]">{c.content_type}</Badge>
                    </div>
                    {c.caption && <p className="text-xs text-muted-foreground line-clamp-2">{c.caption}</p>}
                    {c.hashtags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {c.hashtags.map((h: string, i: number) => (
                          <span key={i} className="text-[10px] text-primary">#{h}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      {format(new Date(c.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {c.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => updateCampaignStatus(c.id, "scheduled")}>
                        <Clock className="h-3 w-3" />
                      </Button>
                    )}
                    {c.status === "scheduled" && (
                      <Button size="sm" variant="outline" onClick={() => updateCampaignStatus(c.id, "published")}>
                        <Send className="h-3 w-3" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => updateCampaignStatus(c.id, "archived")}>
                      <Archive className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCampaign(c.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ============ CALENDAR ============ */}
      {activeSection === "calendar" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" /> Calendário de Conteúdo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                <Input
                  placeholder="Título do post"
                  value={newCalendarItem.title}
                  onChange={e => setNewCalendarItem(p => ({ ...p, title: e.target.value }))}
                />
                <Input
                  type="date"
                  value={newCalendarItem.date}
                  onChange={e => setNewCalendarItem(p => ({ ...p, date: e.target.value }))}
                />
                <Input
                  type="time"
                  value={newCalendarItem.time}
                  onChange={e => setNewCalendarItem(p => ({ ...p, time: e.target.value }))}
                />
                <Select value={newCalendarItem.platform} onValueChange={v => setNewCalendarItem(p => ({ ...p, platform: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={saveCalendarItem}>
                  <Plus className="h-4 w-4 mr-1" />Adicionar
                </Button>
              </div>

              <div className="bg-secondary/20 rounded-lg p-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAiType("calendar");
                    setActiveSection("ai_studio");
                    toast({ title: "Use o Estúdio IA para gerar um calendário completo!" });
                  }}
                >
                  <Sparkles className="h-3 w-3 mr-1" />Gerar Calendário com IA
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-2">
            {calendarItems.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">Calendário vazio</CardContent></Card>
            ) : calendarItems.map(item => (
              <Card key={item.id}>
                <CardContent className="py-3 flex items-center gap-3">
                  {getPlatformIcon(item.platform)}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium truncate">{item.title}</h4>
                    {item.notes && <p className="text-xs text-muted-foreground truncate">{item.notes}</p>}
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p className="font-medium">{item.date}</p>
                    {item.time && <p>{item.time}</p>}
                  </div>
                  {getStatusBadge(item.status)}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ============ ANALYTICS ============ */}
      {activeSection === "analytics" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Impressões", value: "11.6K", icon: Eye, delta: "+12%" },
              { label: "Cliques", value: "924", icon: MousePointerClick, delta: "+8%" },
              { label: "Engajamento", value: "514", icon: TrendingUp, delta: "+23%" },
              { label: "Compartilhamentos", value: "89", icon: Share2, delta: "+5%" },
            ].map((kpi, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <kpi.icon className="h-4 w-4 text-primary" />
                    <span className="text-[10px] text-emerald-500 font-medium">{kpi.delta}</span>
                  </div>
                  <p className="text-xl font-bold">{kpi.value}</p>
                  <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">Performance Semanal</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="impressions" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.1} name="Impressões" />
                    <Area type="monotone" dataKey="clicks" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} name="Cliques" />
                    <Area type="monotone" dataKey="engagement" stroke="#10b981" fill="#10b981" fillOpacity={0.1} name="Engajamento" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Distribuição por Plataforma</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={platformDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {platformDistribution.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {platformDistribution.map((p, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i] }} />
                      {p.name}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Campanhas vs Conversões</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="clicks" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Cliques" />
                  <Bar dataKey="engagement" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} name="Conversões" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ HISTORY ============ */}
      {activeSection === "history" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" /> Histórico de Gerações IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            {aiLogs.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-8">Nenhuma geração registrada</p>
            ) : (
              <div className="space-y-3">
                {aiLogs.map(log => (
                  <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/20 hover:bg-secondary/30 transition-colors">
                    <div className="mt-0.5">
                      {log.result_type === "image" ? <Image className="h-4 w-4 text-pink-500" /> :
                       log.result_type === "video_script" ? <Film className="h-4 w-4 text-purple-500" /> :
                       log.result_type === "ideas" ? <Lightbulb className="h-4 w-4 text-amber-500" /> :
                       <PenTool className="h-4 w-4 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px]">{log.result_type}</Badge>
                        {log.platform && getPlatformIcon(log.platform)}
                        <span className="text-[10px] text-muted-foreground">
                          {format(new Date(log.created_at), "dd/MM HH:mm", { locale: ptBR })}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{log.prompt}</p>
                      {log.result_content && (
                        <p className="text-xs mt-1 line-clamp-2">{log.result_content}</p>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => {
                      setAiResult(log.result_content || "");
                      setActiveSection("ai_studio");
                    }}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminMarketingTab;
